import React from 'react';
import { startSession } from 'utils/session.utils';
import { useGoogleLogin } from '@react-oauth/google';
import Models from 'imports/models.import';
import { setUser } from 'utils/redux.utils';
import { useNavigate } from 'react-router-dom';
import { Assets, Colors, FontFamily, PrimaryButton } from 'utils/imports.utils';
import { toastifyError, useSetState } from 'utils/functions.utils';
import { ButtonTypes, ButtonSize, GoogleAuthMethod } from 'utils/interface.utils';
import { STRINGS } from 'constants/strings.constant';

interface IGoogleAuthProps {
    method: GoogleAuthMethod;
    // Set on the sign-up screen once the person has agreed to the Terms & Conditions and Content Rules.
    acceptedTerms?: boolean;
    // Called instead of opening the popup when the sign-up screen has not had the terms accepted.
    onTermsMissing?: () => void;
}

const GoogleAuth = (props: IGoogleAuthProps) => {
    const navigate = useNavigate();
    // `activity` is true from the click until the attempt ends, so a double click cannot open two popups.
    const [state, setState] = useSetState({ activity: false });

    const openGooglePopup = useGoogleLogin({
        flow: "auth-code",
        onSuccess: async (codeResponse: { code: string }) => {
            try {
                const result = await Models.auth.google({
                    code: codeResponse.code,
                    ...(props.acceptedTerms ? { accepted_terms: true } : {}),
                });
                if (!result.token || !result.data) {
                    toastifyError(STRINGS.googleAuth.genericError);
                    return;
                }
                startSession(result.token, result.data);
                setUser(result.data);
                navigate(result.data.role === 'admin' ? '/admin' : '/welcome');
            } catch (error) {
                // The model rejects with the server's message when there is one (e.g. the email is already registered).
                toastifyError(typeof error === 'string' ? error : STRINGS.googleAuth.genericError);
            } finally {
                setState({ activity: false });
            }
        },
        onError: () => {
            toastifyError(STRINGS.googleAuth.genericError);
            setState({ activity: false });
        },
        // The user closed the popup without choosing an account.
        onNonOAuthError: () => setState({ activity: false }),
    });

    const handleGoogleButtonClick = () => {
        if (state.activity) {
            return;
        }
        if (props.method === GoogleAuthMethod.SIGNUP && !props.acceptedTerms) {
            props.onTermsMissing?.();
            return;
        }
        setState({ activity: true });
        openGooglePopup();
    };

return (
        <PrimaryButton
            onClick={handleGoogleButtonClick}
            disabled={state.activity}
            left_icon={Assets.google_logo}
            color={Colors.googleColor}
            type={ButtonTypes.OUTLINED}
            size={ButtonSize.LARGE}
            fontFamily={FontFamily.semiBold}
            lineHeight="28px"
            text={props.method === GoogleAuthMethod.SIGNUP ? "Sign up with google" : "Sign in with google"}
        />
)};

export default GoogleAuth;
